import { PERMISSIONS } from '@ibe/constants';
import { Button, ConfirmDialog, StatusBadge, toneForStatus } from '@ibe/ui';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { AuditTrailPanel } from '../../../components/resource-detail/audit-trail-panel.js';
import { DetailField, DetailGrid } from '../../../components/resource-detail/detail-grid.js';
import { ResourceDetailPage } from '../../../components/resource-detail/resource-detail-page.js';
import { useRoomType } from '../api/queries.js';
import { useSoftDeleteRoomType } from '../api/mutations.js';

/**
 * One room type.
 *
 * The shell owns loading, errors, breadcrumbs and the correlation id. This file
 * only names the fields.
 */

export function RoomTypeDetailPage(): React.JSX.Element {
  const roomTypeId = useParams().roomTypeId ?? '';
  const navigate = useNavigate();
  const query = useRoomType(roomTypeId);

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const softDelete = useSoftDeleteRoomType();

  const onDelete = async (): Promise<void> => {
    if (query.data === undefined) return;
    await softDelete.mutateAsync({ roomTypeId, version: query.data.version });
    setConfirmingDelete(false);
    void navigate(`/room-types?propertyId=${encodeURIComponent(query.data.propertyId)}`);
  };

  return (
    <ResourceDetailPage
      query={query}
      permission={PERMISSIONS.ROOM_TYPE_READ}
      parent={{ to: '/room-types', label: 'Room types' }}
      title={(data) => data.name}
      actions={
        <>
          <PermissionGuard permission={PERMISSIONS.ROOM_TYPE_UPDATE}>
            <Button asChild variant="outline">
              <Link to={`/room-types/${roomTypeId}/edit`}>Edit</Link>
            </Button>
          </PermissionGuard>
          <PermissionGuard permission={PERMISSIONS.ROOM_TYPE_DELETE}>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmingDelete(true);
              }}
            >
              Delete
            </Button>
          </PermissionGuard>
        </>
      }
    >
      {(data) => (
        <>
          <ConfirmDialog
            open={confirmingDelete}
            onOpenChange={setConfirmingDelete}
            title="Delete room type"
            description={`${data.name} will be removed from this list. Existing reservations and rooms that reference it are retained.`}
            confirmLabel="Delete"
            destructive
            loading={softDelete.isPending}
            onConfirm={() => {
              void onDelete();
            }}
          />

          <DetailGrid>
            <DetailField label="Code">{data.code}</DetailField>
            <DetailField label="Status">
              <StatusBadge tone={toneForStatus(data.status)}>{data.status}</StatusBadge>
            </DetailField>
            <DetailField label="Display name">{data.displayName}</DetailField>
            <DetailField label="Description">{data.description}</DetailField>
            <DetailField label="Maximum occupancy">{String(data.maxOccupancy)}</DetailField>
            <DetailField label="Size (m²)">{data.sizeInSqm}</DetailField>
            <DetailField label="Bed type">{data.bedType}</DetailField>
            <DetailField label="Total number of rooms">{String(data.totalNoOfRooms)}</DetailField>
            <DetailField label="PMS room type id">{data.pmsRoomTypeId}</DetailField>
            <DetailField label="Version">{String(data.version)}</DetailField>
            <DetailField label="Inventory">
              {data.totalRooms === 0 ? (
                'No rooms'
              ) : data.sellableRooms === data.totalRooms ? (
                <span>{data.totalRooms}</span>
              ) : (
                <span>
                  <span className="font-medium">{data.sellableRooms}</span>
                  <span className="text-muted-foreground"> of {data.totalRooms} sellable</span>
                </span>
              )}
            </DetailField>
          </DetailGrid>
          <AuditTrailPanel entityType="room_type" entityId={data.id} />
        </>
      )}
    </ResourceDetailPage>
  );
}
